/* eslint-disable */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  GenericTable,
  GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { ReactComponent as IconEye } from "@assets/Icons/Icon_Eye.svg";
import {
  getSupportTickets,
  getSupportTicketById,
  getKontrolSupportTickets,
  getSupportKontrolTicketById,
  postReplyTicket,
  downloadAttachment,
} from "./request/request";
import { getStatusText, getTicketColumns, SupportTicket } from "./RequestData";
import { TicketDetail, Conversation } from "./TicketDetail";
import ChangeRequestModal from "./components/ChangeRequestModal";
import IncidentReportModal from "./components/IncidentReportModal";
import FeatureRequestModal from "./components/FeatureRequestModal";
import HelpRequestModal from "./components/HelpRequestModal";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import {
  buildPredicate,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";
import {
  makeSupportFields,
  TicketPriorityTag,
  TicketStatusTag,
  TicketTypeTag,
} from "./SupportFields";
import { normalizeFilters } from "kontrolEdge/helpers/normalizeFilters";
import TicketDetailsModal from "./components/TicketDetailsModal";

export function RequestPage() {
  const { isAuthenticated, isLoading } = useAuth0();
  const api = useAxiosAuth0();
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const tenantType = useAppSelector(
    (s) =>
      s.userProfile.TenantType as
        | "GLOBAL_ADMIN"
        | "PARTNER"
        | "CUSTOMER"
        | undefined
  );
  const shouldPassTenantId =
    tenantType !== "CUSTOMER" && Number.isFinite(tenantId);

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | number>("");
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const [isChangeRequestModalOpen, setIsChangeRequestModalOpen] =
    useState(false);
  const [isIncidentReportModalOpen, setIsIncidentReportModalOpen] =
    useState(false);
  const [isFeatureRequestModalOpen, setIsFeatureRequestModalOpen] =
    useState(false);
  const [isHelpRequestModalOpen, setIsHelpRequestModalOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [isReplyLoading, setIsReplyLoading] = useState(false);
  const [isDownloadingAttachment, setIsDownloadingAttachment] = useState(false);

  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const isViewingAllCustomers = tenantId === partnerId;
  // Check if the selected product is "Kontrol Edge"
  const globalProduct = useAppSelector(
    (state) => state.product.selectedProduct
  );
  const isKontrolEdge = globalProduct === "Kontrol Edge";
  const ticketsColumns = getTicketColumns(globalProduct);

  const fetchTickets = useCallback(
    async (signal?: AbortSignal) => {
      if (isLoading) return;
      if (!isAuthenticated) {
        console.warn("User not authenticated; skipping fetch.");
        return;
      }
      if (!tenantId) {
        console.log("No tenantId yet; skipping fetch...");
        return;
      }

      setIsFetching(true);
      try {
        let shaped: SupportTicket[] = [];

        if (isKontrolEdge) {
          const resp = await getKontrolSupportTickets(api, {
            tenantId: shouldPassTenantId ? tenantId : undefined,
            signal,
          });
          shaped = resp.data.tickets.map((item: any) => ({
            ...item,
            id: item.id,
            key: item.key,
            type: item.type,
            subject: item.summary,
            priority: item.priority,
            status: item.status,
          }));
        } else {
          const resp = await getSupportTickets(api, tenantId);
          shaped = (resp.data as SupportTicket[]).map((item: any) => {
            // Ensure id is properly set for CTX Reflector ticket number display
            const ticketId = item.id ?? item._id ?? item.ticketId ?? "";
            return {
              ...item,
              id: ticketId ? String(ticketId) : "",
              key: item.key || ticketId || "",
              type: item.type || "",
              subject: item.subject || "",
              priority: item.priority ?? 0,
              status: item.status || {},
            };
          });
        }

        if (signal?.aborted) return; // bail if canceled mid-flight
        setTickets(shaped);
      } catch (err: any) {
        if (err?.name !== "CanceledError" && err?.message !== "canceled") {
          console.error("Error fetching tickets =>", err);
        }
      } finally {
        if (!signal?.aborted) setIsFetching(false);
      }
    },
    [api, tenantId, isKontrolEdge, isAuthenticated, isLoading]
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchTickets(controller.signal);
    return () => controller.abort();
  }, [fetchTickets]);

  const handleSeeDetails = (ticketId: string | number) => {
    setSelectedTicketId(ticketId);
    setIsDetailsOpen(true);
  };

  // Provide a uniform fetcher for the modal
  const fetchTicketById = useCallback(
    async (ticketId: string | number): Promise<TicketDetail> => {
      const resp = isKontrolEdge
        ? await getSupportKontrolTicketById(api, ticketId)
        : await getSupportTicketById(api, ticketId);

      return {
        ...resp.data,
        conversations: (resp.data.conversations || []).map(
          parseConversationIfNeeded
        ),
      } as TicketDetail;
    },
    [api, isKontrolEdge]
  );

  // -------------------------------
  // Handle sending a reply
  // -------------------------------
  const handleSendReply = useCallback(
    async (
      id: string | number,
      msg: string,
      files: File[] = []
    ): Promise<void> => {
      if (id == null) return;
      try {
        setIsReplyLoading(true);
        await postReplyTicket(api, id, { comment: msg.trim(), files });
        // No refetch here; the modal will do it
        setReplyText("");
        setReplyAttachments([]);
      } finally {
        setIsReplyLoading(false);
      }
    },
    [api]
  );

  const handleDownloadAttachment = async (
    ticketId: string | number,
    attachment: { id: string; filename?: string; name?: string }
  ) => {
    try {
      setIsDownloadingAttachment(true);
      const blob = await downloadAttachment(api, ticketId, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const filename = attachment.filename || attachment.name || "attachment";
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading attachment =>", err);
    } finally {
      setIsDownloadingAttachment(false);
    }
  };

  const supportFields = useMemo(
    () => makeSupportFields(isViewingAllCustomers, isKontrolEdge),
    [isViewingAllCustomers, isKontrolEdge]
  );

  const suggestedKeys = useMemo(
    () => supportFields.map((f) => f.label),
    [supportFields]
  );

  const normalized = useMemo(
    () =>
      normalizeFilters<SupportTicket>(activeFilters, supportFields, {
        defaultStringToContains: true,
        quotedStringsExact: true,
      }),
    [activeFilters, supportFields]
  );

  const predicate = useMemo(
    () => buildPredicate<SupportTicket>(normalized, supportFields),
    [normalized, supportFields]
  );

  const filteredData = useMemo(
    () => tickets.filter(predicate),
    [tickets, predicate]
  );

  //New helpers:
  const looksLikeSenderPrefix = (body: string) => {
    if (!body) return false;
    const idx = body.indexOf(":");
    // must have a colon early on (avoid "http://", "note: blah", etc.)
    if (idx === -1 || idx > 60) return false;
    const head = body.slice(0, idx).trim();
    // Reject if there's a newline before the colon
    if (/\r|\n/.test(head)) return false;
    // Heuristic for "name-ish" header (letters/spaces/accents/punct)
    return /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ' .-]{2,40}$/.test(head);
  };

  const parseConversationIfNeeded = (conv: Conversation): Conversation => {
    const body = conv.body_text || "";
    // Only try to parse when neither sender nor name exists
    if (!conv.sender && !conv.name && looksLikeSenderPrefix(body)) {
      const idx = body.indexOf(":");
      return {
        ...conv,
        sender: body.slice(0, idx).trim(),
        body_text: body.slice(idx + 1).trim(),
      };
    }
    return conv;
  };

  return (
    <div className="relative p-4 overflow-hidden flex flex-col h-full">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <div
          className="
          grid
          grid-cols-1       
          xs375:grid-cols-2  
          md:flex md:flex-row
          items-center 
          gap-2
          "
        >
          <GenericButton
            label="Change Request"
            iconLeftName="exclamation"
            variant="brand"
            onClick={() => setIsChangeRequestModalOpen(true)}
          />
          <GenericButton
            label="Incident Report"
            iconLeftName="headphones"
            variant="brand"
            onClick={() => setIsIncidentReportModalOpen(true)}
          />
          <GenericButton
            label="Feature Request"
            iconLeftName="lightbulb"
            variant="brand"
            onClick={() => setIsFeatureRequestModalOpen(true)}
          />
          <GenericButton
            label="Help Request"
            iconLeftName="question-mark"
            variant="brand"
            onClick={() => setIsHelpRequestModalOpen(true)}
          />
        </div>
      </div>

      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="w-full max-w-full sm:max-w-[520px] my-5">
                  <GenericSearchbar
                    placeholder="Find by attribute or value"
                    suggestedKeys={suggestedKeys}
                    onFiltersChange={setActiveFilters}
                    liveFiltering
                    debounceMs={200}
                  />
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                <GenericTable
                  columns={ticketsColumns.map((col) => {
                    if (col.header === "Type") {
                      return {
                        ...col,
                        renderCell: (row: SupportTicket) =>
                          row.type ? <TicketTypeTag value={row.type} /> : "—",
                      };
                    }
                    if (col.header === "Ticket N°") {
                      return {
                        ...col,
                        renderCell: (row: SupportTicket) => {
                          const ticketNumber = globalProduct === "CTX Reflector" ? row.id : row.key;
                          return (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSeeDetails(row.id);
                              }}
                              className="
                                text-semantic-text-brand
                                font-bold
                                underline
                                hover:opacity-80
                                cursor-pointer
                                text-left
                              "
                            >
                              {ticketNumber || "—"}
                            </button>
                          );
                        },
                      } as GenericTableColumn<SupportTicket>;
                    }
                    if (col.header === "Status") {
                      return {
                        ...col,
                        renderCell: (row: SupportTicket) => {
                          const st = getStatusText(row.status);
                          return st !== "—" ? (
                            <TicketStatusTag value={st} />
                          ) : (
                            "—"
                          );
                        },
                      };
                    }
                    if (col.header === "Priority") {
                      return {
                        ...col,
                        renderCell: (row: SupportTicket) =>
                          Number.isFinite(row.priority) ? (
                            <TicketPriorityTag value={Number(row.priority)} />
                          ) : (
                            "—"
                          ),
                      };
                    }
                    if (col.header === "Details") {
                      return {
                        ...col,
                        renderCell: (row: SupportTicket) => (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSeeDetails(row.id);
                            }}
                            className="
                  flex items-center justify-center
                  border border-semantic-border-secondary
                  rounded
                  text-semantic-text-secondary
                  hover:bg-semantic-state-active
                  w-10 h-10
                "
                          >
                            <IconEye className="w-6 h-6" />
                          </button>
                        ),
                      } as GenericTableColumn<SupportTicket>;
                    }
                    return col;
                  })}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[10, 20, 30]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                  maxTableBodyHeight="100%"
                />
              </div>
            </div>
          }
        />
      </div>

      {isFetching && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/50 z-[9999]">
          <h2 className="text-xl font-semibold mb-4 text-white">
            Processing request...
          </h2>
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500" />
        </div>
      )}

      <TicketDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        ticketId={selectedTicketId}
        fetchTicketById={fetchTicketById}
        onDownloadAttachment={handleDownloadAttachment}
        onSendReply={handleSendReply}
        isKontrolEdge={isKontrolEdge}
      />

      <ChangeRequestModal
        isOpen={isChangeRequestModalOpen}
        onClose={() => setIsChangeRequestModalOpen(false)}
        onTicketCreated={fetchTickets}
      />
      <IncidentReportModal
        isOpen={isIncidentReportModalOpen}
        onClose={() => setIsIncidentReportModalOpen(false)}
        onTicketCreated={fetchTickets}
      />
      <FeatureRequestModal
        isOpen={isFeatureRequestModalOpen}
        onClose={() => setIsFeatureRequestModalOpen(false)}
        onTicketCreated={fetchTickets}
      />
      <HelpRequestModal
        isOpen={isHelpRequestModalOpen}
        onClose={() => setIsHelpRequestModalOpen(false)}
        onTicketCreated={fetchTickets}
      />
    </div>
  );
}

export default RequestPage;
